import { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { loginPaciente } from "../../apiClient/rutes/usuarios";
import { getPaciente } from "../../apiClient/rutes/paciente";
import useTituloPagina from "../../hooks/tituloPagina";
import {
  requiereActualizarCorreo,
  SESSION_EMAIL_OBLIGATORIO_KEY,
  SESSION_PACIENTE_KEY,
} from "../../utils/pacienteEmail";

const useLoginForm = () => {
  const [cargando, setCargando] = useState(false);
  const [blnVerPassword, setBlnVerPassword] = useState(false);
  const [usuario, setUsuario] = useState({ usuario: "", password: "", token: "" });
  const [recordarme, setRecordarme] = useState(false);
  const [urlAxioPP, setUrlAxio] = useState("");
  const [tituloPagina, setTituloPagina] = useState("");
  const [errorUsuario, setErrorUsuario] = useState(false);
  const [errorPassword, setErrorPassword] = useState(false);
  const [msjErrorPassword, setMsjErrorPassword] = useState("");
  const [toast, setToast] = useState({ open: false, mensaje: "", tipo: "" });
  const history = useHistory();
  useTituloPagina();

  const mostrarNotificacion = (abrir, mensaje, tipo) => {
    let notificacion = {};
    if (abrir) {
      notificacion = { open: true, mensaje: mensaje, tipo: tipo };
    } else {
      notificacion = { open: false, mensaje: "", tipo: "" };
    }
    setToast(notificacion);
  };

  const handleChangeVisibilityOfPassword = () => {
    setBlnVerPassword(!blnVerPassword);
  };

  // Funciones para manejar "Recordarme"
  const guardarCredenciales = (usuario, password) => {
    if (recordarme) {
      localStorage.setItem("ppRecordarUsuario", usuario);
      localStorage.setItem("ppRecordarPassword", password);
      localStorage.setItem("ppRecordarme", "true");
    } else {
      localStorage.removeItem("ppRecordarUsuario");
      localStorage.removeItem("ppRecordarPassword");
      localStorage.removeItem("ppRecordarme");
    }
  };

  const cargarCredencialesGuardadas = () => {
    const recordarmeGuardado = localStorage.getItem("ppRecordarme") === "true";
    if (recordarmeGuardado) {
      const usuarioGuardado = localStorage.getItem("ppRecordarUsuario") || "";
      const passwordGuardado = localStorage.getItem("ppRecordarPassword") || "";
      setRecordarme(true);
      setUsuario({ usuario: usuarioGuardado, password: passwordGuardado, token: "" });
    }
  };

  const handleChangeRecordarme = (e) => {
    setRecordarme(e.target.checked);
  };

  useEffect(() => {
    setUrlAxio(localStorage.getItem("urlAxioPP"));
    setTituloPagina(localStorage.getItem("nombreClinica"));
    document.title = localStorage.getItem("tituloWeb");
    sessionStorage.removeItem(SESSION_PACIENTE_KEY);
    sessionStorage.removeItem(SESSION_EMAIL_OBLIGATORIO_KEY);
    
    // Cargar credenciales guardadas si existe "recordarme"
    cargarCredencialesGuardadas();
    // eslint-disable-next-line
  }, []);


  // Login y obtención de paciente usando API centralizada
  const loginPacienteHandler = async (usuarioInput, passwordInput) => {
    try {
      const token = await loginPaciente(usuarioInput, passwordInput);
      return token;
    } catch (error) {
      setErrorPassword(true);
      setMsjErrorPassword("Documento o contraseña incorrecto");
      mostrarNotificacion(true, "Error: " + (error?.response?.data || error?.message), "rojo");
    }
  };

  const obtenerPacienteHandler = async (token, documento) => {
    try {
      const data = await getPaciente(documento, token);
      return {
        usuario: usuario.usuario,
        password: usuario.password,
        token: token,
        codigo: data.codigo,
        hc: data.hc,
        documentoNro: data.documentoNro,
        documentoTipo: data.documentoTipo,
        documentoTipoNombre: data.documentoTipoNombre,
        nombre: data.nombre,
        apellido: data.apellido,
        mutual: data.mutual,
        mutualNombre: data.mutualNombre,
        telefono: data.telefono,
        celular: data.celular,
        email: data.email,
        mutualAfiliado: data.mutualAfiliado,
        nacimiento: data.nacimiento,
      };
    } catch (error) {
      mostrarNotificacion(
        true,
        "Se produjo un error al intentar consultar los datos del paciente",
        "rojo"
      );
    }
  };


  const handleClickLogin = async () => {
    setCargando(true);
    if (usuario.usuario !== "" && usuario.password !== "") {
      const token = await loginPacienteHandler(usuario.usuario, usuario.password);
      if (token) {
        const paciente = await obtenerPacienteHandler(token, usuario.usuario);
        if (paciente) {
          setUsuario(paciente);
          sessionStorage.setItem(SESSION_PACIENTE_KEY, JSON.stringify(paciente));

          if (requiereActualizarCorreo(paciente)) {
            sessionStorage.setItem(SESSION_EMAIL_OBLIGATORIO_KEY, "true");
            history.push("/portal/Datos");
          } else {
            sessionStorage.removeItem(SESSION_EMAIL_OBLIGATORIO_KEY);
            mostrarNotificacion(true, "Sesión iniciada correctamente", "verde");
            history.push("/portal/Inicio");
          }
        }
      }
    } else {
      if (usuario.usuario !== "") setErrorUsuario(true);
      if (usuario.password !== "") {
        setErrorPassword(true);
        setMsjErrorPassword("Debe ingresar su contraseña");
      }
    }
    setCargando(false);
  };

  const handleClickSubmit = () => {
    setCargando(true);
    
    // Guardar credenciales si "recordarme" está marcado
    guardarCredenciales(usuario.usuario, usuario.password);
    
    handleClickLogin();
    setCargando(false);
  };

  const handleChangeUsuario = (e) => {
    const value = e.target.value;
    const filteredValue = value.replace(/[^0-9]+/g, "");
    setErrorUsuario(false);
    setUsuario({ usuario: filteredValue, password: usuario.password });
  };

  const handleChangePassword = (e) => {
    const value = e.target.value;
    const filteredValue = value.replace(/[^0-9]+/g, "");
    setErrorPassword(false);
    setUsuario({ usuario: usuario.usuario, password: filteredValue });
  };

  const handleClickCopiarTelefono = () => {
    const phoneNumber = "3541-422420";
    navigator.clipboard.writeText(phoneNumber);
    mostrarNotificacion(true, "Se ha copiado el numero de telefono a su portapapeles", "");
  };

  return {
    cargando,
    blnVerPassword,
    usuario,
    recordarme,
    urlAxioPP,
    tituloPagina,
    errorUsuario,
    errorPassword,
    msjErrorPassword,
    toast,
    mostrarNotificacion,
    handleChangeVisibilityOfPassword,
    handleClickLogin,
    handleClickSubmit,
    handleChangeUsuario,
    handleChangePassword,
    handleChangeRecordarme,
    handleClickCopiarTelefono,
    setErrorUsuario,
    setErrorPassword,
    setUsuario,
    setToast,
  };
};

export default useLoginForm;
